import React, { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearch } from 'wouter';

import { AuthApiError, signup } from '../../api/auth';
import { requestJson } from '../../api/http';
import { WaitlistForm } from './WaitlistForm';
import { PasswordField, passwordAcceptable } from '../../components/PasswordField';
import { offerPriceLabel } from '../../lib/offerPrice';
import {
  clearPendingOffer,
  fetchOfferPreview,
  type OfferPreview,
  setPendingOffer,
} from '../../lib/pendingOffer';

type OfferState =
  | { kind: 'none' }
  | { kind: 'loading' }
  | { kind: 'resolved'; offer: OfferPreview; saved: boolean }
  | { kind: 'ignored' };

function safeWaitlistHref(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && parsed.host && !parsed.username && !parsed.password
      ? value
      : null;
  } catch {
    return null;
  }
}

function returnToAfterSignup(offerState: OfferState): string {
  return offerState.kind === 'resolved' && offerState.saved
    ? '/billing/checkout'
    : '/projects';
}

export function SignupPage() {
  const search = useSearch();
  const query = useMemo(() => {
    const params = new URLSearchParams(search);
    const offers = params.getAll('offer');
    const intervalValues = params.getAll('interval');
    return {
      hasOffer: params.has('offer'),
      offerId: offers.length === 1 ? offers[0] : null,
      hasInterval: params.has('interval'),
      interval: intervalValues.length === 1 ? intervalValues[0] : null,
    };
  }, [search]);
  const [waitlistMode, setWaitlistMode] = useState(false);
  const [waitlistAvailable, setWaitlistAvailable] = useState(false);
  useEffect(() => { requestJson('/v1/auth/waitlist/mode').then(value => { const enabled = (value as { waitlist: boolean }).waitlist; setWaitlistAvailable(enabled); setWaitlistMode(enabled); }).catch(() => {}); }, []);
  const [offerState, setOfferState] = useState<OfferState>({ kind: 'none' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [inviteOnly, setInviteOnly] = useState(false);
  const [inviteWaitlistUrl, setInviteWaitlistUrl] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    clearPendingOffer();
    setOfferState(query.hasOffer ? { kind: 'loading' } : { kind: 'none' });
    if (!query.hasOffer) return () => { cancelled = true; };
    if (!query.offerId || !query.offerId.trim()) {
      setOfferState({ kind: 'ignored' });
      return () => { cancelled = true; };
    }

    const offerId = query.offerId.trim();
    fetchOfferPreview(offerId)
      .then((offer) => {
        if (cancelled) return;
        if (offer.billing_period && offer.billing_period !== 'month') {
          throw new Error('the selected interval is unavailable');
        }
        const saved = setPendingOffer({ id: offer.id, interval: 'month' });
        setOfferState({ kind: 'resolved', offer, saved });
      })
      .catch(() => {
        if (cancelled) return;
        clearPendingOffer();
        setOfferState({ kind: 'ignored' });
      });
    return () => { cancelled = true; };
  }, [query.hasOffer, query.offerId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setInviteOnly(false);
    setInviteWaitlistUrl(null);
    try {
      const message = await signup({ email, ...(passwordRequired ? { password } : {}) });
      setSuccessMessage(message);
      setPassword('');
    } catch (caught) {
      if (caught instanceof AuthApiError && caught.code === 'signup_invite_only') {
        setInviteOnly(true);
        setInviteWaitlistUrl(safeWaitlistHref(caught.waitlistUrl));
      } else if (caught instanceof AuthApiError && caught.code === 'password_required') {
        setPasswordRequired(true);
        setError('Choose a password to continue.');
      } else {
        setError(caught instanceof AuthApiError ? caught.message : 'sign-up could not be completed');
      }
    } finally {
      setBusy(false);
    }
  }

  const returnTo = returnToAfterSignup(offerState);
  const signInHref = '/login?return_to=' + encodeURIComponent(returnTo);
  const invalidInterval = query.hasInterval && query.interval !== 'month';

  return (
    <section className="auteur-signup" aria-labelledby="auteur-signup-heading">
      <p className="eyebrow">Create your account</p>
      <h1 id="auteur-signup-heading">Sign up for Auteur Cloud</h1>

      {invalidInterval ? (
        <p className="auteur-signup-notice" role="status">
          Only the monthly interval is available right now. Monthly billing will be used.
        </p>
      ) : null}
      {offerState.kind === 'loading' ? (
        <p className="auteur-signup-notice" role="status">Checking the selected offer…</p>
      ) : null}
      {offerState.kind === 'ignored' ? (
        <p className="auteur-signup-notice" role="status">
          That offer could not be confirmed and was ignored. You can still create an account.
        </p>
      ) : null}
      {offerState.kind === 'resolved' ? (
        <section className="auteur-signup-offer" aria-label="Selected offer">
          <h2>Confirm your selection</h2>
          <p>{offerState.offer.name}</p>
          <p>Price: {offerPriceLabel(offerState.offer)}</p>
          <p>Billed monthly</p>
          {!offerState.saved ? (
            <p className="auteur-signup-notice" role="status">
              This browser could not save the offer for the next step. The account form is still available.
            </p>
          ) : null}
        </section>
      ) : null}

      {waitlistMode ? <><WaitlistForm /><p>Already invited? <button type="button" onClick={() => setWaitlistMode(false)}>Create your account</button></p></> : successMessage ? (
        <div className="auteur-signup-success">
          <p role="status">{successMessage}</p>
          {passwordRequired ? (
            <p>
              Check your email and open the link to confirm your address, then{' '}
              <Link to={signInHref}>sign in and continue</Link>.
            </p>
          ) : (
            <p>
              Next: check your email and open the link we sent. It takes you to a page where you choose
              your password, and then straight into your account. The link can take a minute to arrive;
              check your spam folder if you don’t see it.
            </p>
          )}
          <p>Need another verification email? <Link to="/verify-email">Resend verification</Link>.</p>
        </div>
      ) : (
        <form onSubmit={submit}>
          <label htmlFor="auteur-signup-email">Email</label>
          <input
            id="auteur-signup-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            disabled={busy}
            onChange={(event) => setEmail(event.target.value)}
          />

          {passwordRequired ? (
            <PasswordField
              id="auteur-signup-password"
              name="password"
              label="Password"
              autoComplete="new-password"
              showRules
              value={password}
              disabled={busy}
              onChange={(value) => {
                setPassword(value);
                setError('');
              }}
            />
          ) : (
            <p className="auteur-signup-how" id="auteur-signup-how">
              How it works: enter your email and we’ll send you a link. Open it to confirm the address
              is yours and choose your password (at least 12 characters), and you’re signed in. We ask
              for the password after the link so nobody else can claim your address.
            </p>
          )}

          {error ? <p className="auteur-signup-error" role="alert">{error}</p> : null}
          {inviteOnly ? (
            <p className="auteur-signup-invite" role="status">
              You’re not on the invite list yet — request founding access:{' '}
              {waitlistAvailable ? <button type="button" onClick={() => setWaitlistMode(true)}>Join waitlist</button> : inviteWaitlistUrl ? (
                <a href={inviteWaitlistUrl}>Get founding access</a>
              ) : null}
            </p>
          ) : null}

          <button type="submit" disabled={busy || !email || (passwordRequired && !passwordAcceptable(password))}>
            {busy ? 'Working…' : 'Create account'}
          </button>
        </form>
      )}

      <p><Link to="/login">Already have an account? Sign in</Link></p>
    </section>
  );
}

export default SignupPage;
